// src/layers/imagery/entitledCoverage.js
//
// Coverage at the map center, for both time strips (footprint restyle S-e5):
// SitePlanner's (padplanner/imageryStripRows.js) and Explore's
// (exploreRows.js, through withEntitledAreas). One answer, read the same way.
//
// ── THE AREA IS THE ENTITLED ONE ────────────────────────────────────────────
//
// A capture covers a point where its ENTITLED area holds it: the feature
// GET /imagery/entitled answers for it (authz-api, the tile session's own
// decision: a cell grant is footprint ∩ its stored cells), and for Sentinel-2
// the same in the answer's `reference` array. Not the catalog's footprint
// (GET /imagery/footprints serves a cell grant's whole footprint, REM-692): a
// chip at full strength over ground whose tiles the tile session clips away
// is the strip lying. The point-in-polygon test is coverageAtPoint.js's.
//
// ── THE THREE ANSWERS ───────────────────────────────────────────────────────
//
//   covered   the area holds the point: the chip at full strength
//   none      the answer is in and the area does not hold it, or the answer
//             leaves the capture out (it is not entitled here): dimmed, "no
//             coverage at map center"
//   unknown   no answer yet (or it failed), no center, an unusable geometry,
//             or a synthetic_test capture the answer was not asked to include
//             (the answer carries one only as `include`): drawn normally.
//             coverageAtPoint.js's rule: unknown is never shown as none.
//
// ── ZOOM IN ─────────────────────────────────────────────────────────────────
//
// Below a capture's minzoom the tiler draws nothing (S-e5 recon: dev's 10 cm
// captures at z8-11 are 67-byte PNGs), and SitePlanner's raster source starts
// at the minzoom. A covered capture the map is zoomed out past is NOT dimmed
// (DB, 2026-11-03): full strength, a zoom-in mark, "Zoom in to see this
// capture (from z<minzoom>)". Clicking it selects it and zooms the map to the
// minzoom, keeping the center.
import { geometryCoversPoint } from "./coverageAtPoint";

export const NO_COVERAGE_TEXT = "no coverage at map center";
export const zoomInText = (minzoom) => `Zoom in to see this capture (from z${minzoom})`;

const EMPTY_AREA = Object.freeze({ type: "MultiPolygon", coordinates: Object.freeze([]) });
const isSynthetic = (capture) => capture?.provenance === "synthetic_test";

/**
 * The answer of GET /imagery/entitled as dataset_key -> geometry, over its
 * features and its `reference` array. null until the answer is in, or when it
 * failed: unknown, never "covers nothing".
 * @param {{features?: object[], reference?: object[]}|null} entitled
 * @param {"loading"|"ok"|"error"} status  useEntitledFootprints' status
 * @returns {Map<string, object|null>|null}
 */
export function entitledAreas(entitled, status) {
  if (status !== "ok" || !entitled) return null;
  const areas = new Map();
  for (const f of [...(entitled.features ?? []), ...(Array.isArray(entitled.reference) ? entitled.reference : [])]) {
    const key = f?.properties?.dataset_key;
    if (key) areas.set(key, f.geometry ?? null);
  }
  return areas;
}

/**
 * A capture's coverage at a point.
 * @param {Map<string, object|null>|null} areas  entitledAreas()
 * @param {{dataset_key: string, provenance?: string}} capture
 * @param {[number, number]|null} center  [lng, lat]
 * @returns {"covered"|"none"|"unknown"}
 */
export function coverageAt(areas, capture, center) {
  if (!areas || !center || !capture?.dataset_key) return "unknown";
  if (!areas.has(capture.dataset_key)) return isSynthetic(capture) ? "unknown" : "none";
  const v = geometryCoversPoint(areas.get(capture.dataset_key), center);
  return v === true ? "covered" : v === false ? "none" : "unknown";
}

/**
 * The zoom to go to for a covered capture the map is zoomed out past: its
 * minzoom, or null (not covered, at or past it, or no minzoom or zoom known).
 * @param {{minzoom?: number}} capture
 * @param {"covered"|"none"|"unknown"} coverage
 * @param {number|null} zoom
 */
export function zoomInTo(capture, coverage, zoom) {
  const mz = capture?.minzoom;
  return coverage === "covered" && Number.isInteger(mz) && Number.isFinite(zoom) && zoom < mz ? mz : null;
}

/**
 * Explore's series with each acquisition's geometry replaced by its entitled
 * area, so coverageAtPoint.js's helpers (coverage, Latest, Play, Compare's
 * pair) answer from it: a capture the answer leaves out covers nothing (an
 * empty area); with no answer, or a synthetic one not answered, null
 * (unknown). The catalog's own geometry is never read. New objects; the
 * catalog is not touched.
 * @param {object[]} seriesList
 * @param {Map<string, object|null>|null} areas
 */
export function withEntitledAreas(seriesList, areas) {
  return seriesList.map((s) => ({
    ...s,
    acquisitions: (s.acquisitions ?? []).map((a) => {
      let geometry = null;
      if (areas) geometry = areas.has(a.dataset_key) ? areas.get(a.dataset_key) : isSynthetic(a) ? null : EMPTY_AREA;
      return { ...a, geometry };
    }),
  }));
}
